import { Controller } from "@hotwired/stimulus";

// Generic syntax highlighter built on highlight.js. Lazy-loads core + the
// requested language so we don't ship ~50KB in the main bundle.
//
// Usage:
//   <pre data-controller="code-highlight"
//        data-code-highlight-language-value="bash"><code>...</code></pre>
//
// New languages: add a case to loadLanguage(). Vite needs static import
// strings to bundle them, so a switch is the right shape here.

type HljsLanguage = (hljs: unknown) => unknown;

async function loadLanguage(name: string): Promise<HljsLanguage | null> {
  switch (name) {
    case "bash":
    case "shell":
      return (await import("highlight.js/lib/languages/bash")).default;
    case "json":
      return (await import("highlight.js/lib/languages/json")).default;
    case "javascript":
      return (await import("highlight.js/lib/languages/javascript")).default;
    case "python":
      return (await import("highlight.js/lib/languages/python")).default;
    default:
      return null;
  }
}

export default class CodeHighlightController extends Controller<HTMLElement> {
  static values = { language: { type: String, default: "plaintext" } };

  declare languageValue: string;

  async connect(): Promise<void> {
    const target =
      (this.element.querySelector("code") as HTMLElement | null) ?? this.element;
    if (target.dataset.highlighted === "yes") return;

    try {
      const { default: hljs } = await import("highlight.js/lib/core");
      const lang = this.languageValue;
      if (lang !== "plaintext" && !hljs.getLanguage(lang)) {
        const def = await loadLanguage(lang);
        if (def) hljs.registerLanguage(lang, def as never);
      }
      target.classList.add(`language-${lang}`);
      hljs.highlightElement(target);
    } catch {
      // hljs not available — plain text is fine
    }
  }
}
